/**
 * Oil & Gas — Temporal Analysis  (Step 2 of the 8-step intelligence loop)
 *
 * Select a pipeline corridor and a date range, then fetch Sentinel-2 satellite
 * imagery via GEE. Displays LST, NDVI die-off, and thermal anomaly indices over
 * time to identify vegetation die-off patterns indicative of subsurface leaks.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Clock, Satellite, RefreshCw, BarChart2, CloudOff } from 'lucide-react';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Alert from '../../components/common/Alert';
import Spinner from '../../components/common/Spinner';
import { satelliteBoundariesApi, satelliteImageryApi } from '../../services/api';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Corridor {
  id: string;
  name: string;
  area_hectares?: number;
  metadata?: { pipeline_id?: string; vertical?: string };
}

interface VegetationIndex {
  index_type: string;
  mean_value: number;
  min_value: number;
  max_value: number;
  std_dev: number;
}

interface ImageryRecord {
  id: string;
  acquisition_date: string;
  satellite: string;
  cloud_cover_percentage: number;
  indices: VegetationIndex[];
}

// ─── Index colour map ─────────────────────────────────────────────────────────

const INDEX_COLOUR: Record<string, string> = {
  NDVI:  'text-green-700 dark:text-green-400',
  LST:   'text-red-700 dark:text-red-400',
  NDBI:  'text-blue-700 dark:text-blue-400',
  NDWI:  'text-cyan-700 dark:text-cyan-400',
};

// ─── Component ────────────────────────────────────────────────────────────────

export const OilGasTemporalAnalysisPage: React.FC = () => {
  const [corridors, setCorridors]   = useState<Corridor[]>([]);
  const [imagery, setImagery]       = useState<ImageryRecord[]>([]);
  const [loading, setLoading]       = useState(true);
  const [fetching, setFetching]     = useState(false);
  const [error, setError]           = useState<string | null>(null);
  const [success, setSuccess]       = useState<string | null>(null);

  const [selectedCorridor, setSelectedCorridor] = useState('');
  const [startDate, setStartDate]               = useState(() => {
    const d = new Date(); d.setMonth(d.getMonth() - 3);
    return d.toISOString().slice(0, 10);
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().slice(0, 10));

  // Load corridors
  useEffect(() => {
    satelliteBoundariesApi.getAll()
      .then((res) => {
        const all: Corridor[] = res.data?.boundaries ?? [];
        setCorridors(all.filter((b) => b.metadata?.vertical === 'oil_gas'));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Load imagery when corridor changes
  const loadImagery = useCallback(async () => {
    if (!selectedCorridor) { setImagery([]); return; }
    setFetching(true); setError(null);
    try {
      const res = await satelliteImageryApi.getImagery(selectedCorridor, { startDate, endDate });
      setImagery(res.imagery ?? []);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Failed to load imagery');
    } finally {
      setFetching(false);
    }
  }, [selectedCorridor, startDate, endDate]);

  useEffect(() => { loadImagery(); }, [loadImagery]);

  // Trigger a fresh GEE fetch
  const handleFetch = async () => {
    if (!selectedCorridor) { setError('Select a corridor first'); return; }
    setFetching(true); setError(null);
    try {
      await satelliteImageryApi.fetchImagery(selectedCorridor, { startDate, endDate });
      setSuccess('Satellite fetch queued — results will appear within 2–3 minutes');
      await loadImagery();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Fetch failed');
    } finally {
      setFetching(false);
    }
  };

  const INPUT_CLS = 'px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';

  return (
    <div className="space-y-6">

      {/* ── Header ── */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <Clock className="w-7 h-7 text-orange-500" /> Temporal Analysis
        </h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Step 2 — Multi-temporal satellite indices: LST, NDVI die-off, NDBI, thermal anomaly layers
        </p>
      </div>

      {error   && <Alert type="error"   onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert type="success" onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* ── Controls ── */}
      <Card padding="sm">
        <div className="flex flex-wrap gap-3 items-end">
          {loading ? (
            <Spinner size="sm" />
          ) : (
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-gray-600 dark:text-gray-400">Corridor</label>
              <select
                className={INPUT_CLS}
                value={selectedCorridor}
                onChange={(e) => setSelectedCorridor(e.target.value)}
              >
                <option value="">Select corridor…</option>
                {corridors.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
          )}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-600 dark:text-gray-400">Start Date</label>
            <input type="date" className={INPUT_CLS} value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-600 dark:text-gray-400">End Date</label>
            <input type="date" className={INPUT_CLS} value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </div>
          <Button onClick={handleFetch} loading={fetching} disabled={!selectedCorridor}>
            <Satellite className="w-4 h-4 mr-1" /> Fetch Imagery
          </Button>
          <Button variant="outline" onClick={loadImagery} disabled={!selectedCorridor || fetching}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </Card>

      {/* ── Results ── */}
      {!selectedCorridor ? (
        <Card>
          <div className="text-center py-10">
            <BarChart2 className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">No corridor selected</p>
            <p className="text-sm text-gray-400 mt-1">Select a corridor above to view satellite timeline</p>
          </div>
        </Card>
      ) : fetching ? (
        <div className="flex justify-center py-14"><Spinner size="lg" /></div>
      ) : imagery.length === 0 ? (
        <Card>
          <div className="text-center py-10">
            <CloudOff className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">No imagery available</p>
            <p className="text-sm text-gray-400 mt-1">Click <strong>Fetch Imagery</strong> to queue a GEE acquisition</p>
          </div>
        </Card>
      ) : (
        <Card padding="none">
          <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700">
            <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
              {imagery.length} satellite acquisition{imagery.length !== 1 ? 's' : ''}
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100 dark:divide-gray-800">
              <thead className="bg-gray-50 dark:bg-gray-700/50">
                <tr>
                  {['Date', 'Satellite', 'Cloud Cover', 'NDVI', 'LST', 'NDBI', 'NDWI'].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {imagery.map((rec) => {
                  const idx = (type: string) => rec.indices.find((i) => i.index_type === type);
                  return (
                    <tr key={rec.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white whitespace-nowrap">
                        {new Date(rec.acquisition_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-600 dark:text-gray-400">{rec.satellite}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{rec.cloud_cover_percentage != null ? Number(rec.cloud_cover_percentage).toFixed(1) : '—'}%</td>
                      {['NDVI', 'LST', 'NDBI', 'NDWI'].map((type) => {
                        const i = idx(type);
                        return (
                          <td key={type} className={`px-4 py-3 text-sm font-mono ${INDEX_COLOUR[type] ?? ''}`}>
                            {i ? Number(i.mean_value).toFixed(3) : '—'}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
};

export default OilGasTemporalAnalysisPage;

// Made with Bob
